import { Option } from '../components/Elements/Dropdown'
import { Date as DateType, CategoryGroup, Priority, ReportDashboardFieldFilter, Status } from '../types'
import { getUnixTime, subHours } from 'date-fns'
import {
  trans, getLocale, CUSTOM_FILTER_HOURS, FILTER_ALL, TIMELINE_CUSTOM_HOURS, TIMELINE_CUSTOM,
  WEATHER_FAHRENHEIT, WEATHER_CELSIUS, TIMELINE_DAYS, TIMELINE_HOURS, STATUS_GROUP_ACTIVE, STATUS_GROUP_ALL, STATUS_GROUP_INACTIVE
} from '../translator'
import formatDistance from 'date-fns/formatDistance'
import parseISO from 'date-fns/parseISO'
import * as dateFnLocales from 'date-fns/locale'

export const IssuesStatusOptions: Option[] = [
  { value: 'all', label: trans(STATUS_GROUP_ALL, {}, 'dashboard') },
  { value: 'active', label: trans(STATUS_GROUP_ACTIVE, {}, 'dashboard') },
  { value: 'inactive', label: trans(STATUS_GROUP_INACTIVE, {}, 'dashboard') }
]

export const TimelineOptions: Option[] = [
  { value: 'all', label: trans(FILTER_ALL, {}, 'dashboard') },
  { value: '1', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 1 }, 'dashboard') },
  { value: '3', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 3 }, 'dashboard') },
  { value: '6', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 6 }, 'dashboard') },
  { value: '12', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 12 }, 'dashboard') },
  { value: '24', label: trans(TIMELINE_CUSTOM_HOURS, { '%count%': 24 }, 'dashboard') },
  { value: 'custom', label: trans(TIMELINE_CUSTOM, {}, 'dashboard') }
]

export const WeatherTempUnits: Option[] = [
  { value: 'celsius', label: trans(WEATHER_CELSIUS, {}, 'dashboard') },
  { value: 'farenheit', label: trans(WEATHER_FAHRENHEIT, {}, 'dashboard') }
]

/**
 * Map Issues Status from the Dropdown Option to the Issues Status Array
 */
export const setPriorityFilters = (status: Option, setFilterStatuses: any) => {
  switch (status) {
    case IssuesStatusOptions[0]:
      return setFilterStatuses(IssuesStatusOptions[0].value)
    case IssuesStatusOptions[1]:
      return setFilterStatuses(IssuesStatusOptions[1].value)
    case IssuesStatusOptions[2]:
      return setFilterStatuses(IssuesStatusOptions[2].value)
    default:
      return setFilterStatuses(IssuesStatusOptions[0].value)
  }
}

export const findStatusGroupOption = (status: string|null) => {
  switch (status) {
    case 'all':
      return IssuesStatusOptions[0]
    case 'active':
      return IssuesStatusOptions[1]
    case 'inactive':
      return IssuesStatusOptions[2]
    default:
      return IssuesStatusOptions[0]
  }
}

export const findActiveStatuses = (statuses: Status[]) => {
  return statuses.filter((status:Status) => !status.is_closing_status) || []
}

export const findCompleteStatuses = (statuses: Status[]) => {
  return statuses.filter((status:Status) => status.is_closing_status) || []
}

export const DoughnutChartColors = [
  '#d7f2f3',
  '#6D8794',
  '#82E4CD',
  '#F2A2B3',
  '#1F7ED6',
  '#FFBB38',
  '#F89F83',
  '#FE90E6',
  '#103F6B',
  '#5DC389',
  '#82DECD',
  '#E79B42',
  '#8FBFEB',
  '#4968D3',
  '#A3DDBC'
]

export const computeColor = (dataIndex: number) => {
  const colorIndex = dataIndex % DoughnutChartColors.length
  return DoughnutChartColors[colorIndex]
}

export const transformFilter = (filter: {type:string, status?:{value, label}, logs:[], priorities?:[], categoryGroups?:[], hideCategoriesWithNoIssues?: boolean, fromDate: number, toDate: number }, prioritiesData: Priority[], categoryGroupsData: CategoryGroup[]) => {
  const diffInSeconds = filter.toDate - filter.fromDate
  const diffInHours = diffInSeconds / 3600
  const ceilDiffInHours = Math.ceil(diffInHours)
  const timeLabel =
    ceilDiffInHours >= 48
      ? trans(TIMELINE_DAYS, { '%count%': Math.ceil(ceilDiffInHours / 24) }, 'dashboard')
      : trans(TIMELINE_HOURS, { '%count%': ceilDiffInHours }, 'dashboard')

  const predefinedTimelineObject = { value: ceilDiffInHours.toString(), label: timeLabel }

  const transformedFilter: ReportDashboardFieldFilter = {
    activeLogsId: [],
    statuses: [], // this will actually be used for status
    timeline: predefinedTimelineObject,
    customTimeline: undefined,
    selectedPriorities: [],
    selectedCategoryGroups: [],
    selectedCategoryGroup: undefined,
    hideEmpty: false,
    fromDate: undefined,
    toDate: undefined,
    statusGroup: 'all' // to satisfy interface, will not be used
  }

  Object.keys(filter).forEach(key => {
    switch (key) {
      case 'status':
        transformedFilter.statuses = []
        transformedFilter.statuses.push(filter.status.value)
        break
      case 'categories':
        // @ts-ignore
        transformedFilter.selectedCategoryGroup = categoryGroupsData.find(availableCategory => filter.categories.some(selectedCategory => selectedCategory.value === availableCategory.id))
        break
      case 'categoryGroups':
        // @ts-ignore
        transformedFilter.selectedCategoryGroups = filter.type === 'issueByCategoryGroup' && filter.categoryGroups.length === 0
          ? categoryGroupsData
        // @ts-ignore
          : filter.categoryGroups.map(selectedCategoryGroup => categoryGroupsData.find(availableCategory => selectedCategoryGroup.value === availableCategory.id))
        break
      case 'hideCategoriesWithNoIssues':
        transformedFilter.hideEmpty = !filter.hideCategoriesWithNoIssues
        break
      case 'priorities':
        // @ts-ignore
        transformedFilter.selectedPriorities = filter.priorities.map(selectedPriority => prioritiesData.find(availablePriority => selectedPriority.value === availablePriority.id))
        break
      case 'logs':
        transformedFilter.activeLogsId = filter.logs.map((log: any) => log.value)
        break
      case 'fromDate':
        transformedFilter.fromDate = filter.fromDate
        break
      case 'toDate':
        transformedFilter.toDate = filter.toDate
    }
  })
  return transformedFilter
}

export const getTextForTimelineFilterInfo = (filter:any) => {
  const translatedTimelineLabel = TimelineOptions.find((option) => option.value === filter.timeline.value)?.label
  return translatedTimelineLabel ?? (filter.customTimeline
    ? trans(CUSTOM_FILTER_HOURS, {
      '%duration%': filter.customTimeline
        ? filter?.customTimeline
        : filter?.timeline?.value
    }, 'dashboard')
    : trans(FILTER_ALL, {}, 'dashboard'))
}

export const fromDateFallbackValue = (customValue?:string, timelineValue?:string, earliestSelectedLogTimestamp?:number) => {
  return ((customValue || timelineValue) && timelineValue !== 'all')
    ? getUnixTime(
      subHours(
        new Date(),
        customValue ? parseInt(customValue) : parseInt(timelineValue)
      )
    )
    : earliestSelectedLogTimestamp !== undefined ? earliestSelectedLogTimestamp : getUnixTime(new Date()) - 360000
}

export const mapDateFormatToDayMonth = (dateFormat: string): string => {
  switch (dateFormat) {
    case 'dd/mm/yyyy':
      return 'dd/MM'
    case 'mm/dd/yyyy':
      return 'MM/dd'
    case 'yyyy-mm-dd':
      return 'MM-dd'
    default:
      return 'dd/MM'
  }
}

export const getTimeFormat = (isUseAmPmString: string, isUseTimezone: boolean = false): string => {
  const isUseAmPm = isUseAmPmString === '1'

  let timeFormat = isUseAmPm ? 'hh:mm a' : 'HH:mm'

  if (isUseTimezone) {
    timeFormat += ' zzz'
  }

  return timeFormat
}

export const getNormalizedAngle = (angle: number) => {
  let normalizedAngle = angle % (Math.PI * 2)
  if (normalizedAngle < 0) {
    normalizedAngle += Math.PI * 2
  }

  return normalizedAngle
}

export const getTimeAgo = (created_at: DateType) => {
  const localeString = getLocale().slice(0, 2)
  const locale = dateFnLocales[localeString] ?? dateFnLocales.enUS
  return formatDistance(parseISO(created_at.date), new Date(), {
    addSuffix: true,
    locale
  })
}
